import { cn } from '@/lib/cn';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

export interface SelectOption {
  label: string;
  value: string;
}

export interface SelectProps {
  options: Array<SelectOption>;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  label?: string;
  error?: string;
  touched?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  onBlur,
  label,
  error,
  touched,
  id,
  name,
  placeholder = '',
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="group relative w-full">
      <div className="relative">
        <SelectPrimitive.Root
          value={value}
          onValueChange={onChange}
          open={isOpen}
          onOpenChange={setIsOpen}
          name={name}
        >
          <SelectPrimitive.Trigger
            id={id}
            onBlur={onBlur}
            className={cn(
              'border-input ring-offset-background focus:border-secondary flex h-14.5 w-full items-center justify-between rounded-[0.625rem] border bg-white px-4 py-4 text-lg transition-all duration-200 focus:border-[0.09375rem] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              touched && error
                ? 'border-destructive focus:border-destructive'
                : '',
              className,
            )}
          >
            <SelectPrimitive.Value placeholder={placeholder} />
            <SelectPrimitive.Icon asChild>
              <ChevronDown className="h-4 w-4 opacity-50" />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className="bg-background border-muted data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 overflow-hidden rounded-md border shadow-md"
              position="popper"
              side="bottom"
              avoidCollisions={false}
              sideOffset={4}
              style={{ width: 'var(--radix-select-trigger-width)' }}
            >
              <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
                <ChevronUp className="h-4 w-4" />
              </SelectPrimitive.ScrollUpButton>
              <SelectPrimitive.Viewport className="max-h-80 min-h-14.5 p-1">
                {options.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    className="focus:bg-secondary focus:text-primary-foreground relative flex w-full cursor-default items-center rounded-sm py-2 pr-2 pl-8 text-sm transition-colors outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50"
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      <SelectPrimitive.ItemIndicator>
                        <Check className="h-4 w-4" />
                      </SelectPrimitive.ItemIndicator>
                    </span>
                    <SelectPrimitive.ItemText className="truncate">
                      {option.label}
                    </SelectPrimitive.ItemText>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
              <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
                <ChevronDown className="h-4 w-4" />
              </SelectPrimitive.ScrollDownButton>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {label && (
          <label
            htmlFor={id}
            className={cn(
              'text-muted-foreground pointer-events-none absolute left-4 flex h-full items-center text-base font-normal transition-all duration-200 ease-out',
              // Floating positioning
              value || isOpen
                ? 'bg-background -top-3 z-10 h-auto px-1 text-xs'
                : 'top-0',
              isOpen ? 'text-secondary' : '',
              touched && error ? 'text-destructive' : '',
            )}
          >
            {label}
          </label>
        )}

        {touched && error && (
          <div className="text-destructive pointer-events-none absolute top-1/2 right-10 -translate-y-1/2 text-sm font-light">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export { Select };
